import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ArrowRightLeft,
  Copy,
  Check,
  Download,
  Settings2,
  FilterX,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn, formatDateTime } from '@/lib/utils';
import { SensorStatusBadge } from './SensorStatusBadge';
import { SensorAccountsFilterCombobox } from './SensorAccountsFilterCombobox';
import { ALL_SENSOR_STATUS_MAP, ALL_SENSOR_STATUS_CODES } from '../types';
import type { Sensor } from '../types';

// =============================================================================
// Column Definition
// =============================================================================

interface SensorTableHelpers {
  onEdit: (sensorId: string) => void;
  onReassign: (sensor: Sensor) => void;
  onImageClick: (url: string, name: string) => void;
}

export interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: 'text' | 'date-range' | 'select' | 'number-range';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T, helpers: SensorTableHelpers) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

// =============================================================================
// Helpers
// =============================================================================

function decodeHtmlEntities(value: string): string {
  if (!value || !value.includes('&')) return value;
  if (typeof document === 'undefined') {
    return value
      .replace(/&gt;/g, '>')
      .replace(/&lt;/g, '<')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;|&apos;/g, '\'');
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

function getPopulatedName(ref: { _id: string; name: string } | string | null | undefined): string {
  if (!ref) return '';
  if (typeof ref === 'string') return decodeHtmlEntities(ref);
  return decodeHtmlEntities(ref.name || '');
}


function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-6 w-6 p-0"
      onClick={handleCopy}
      aria-label={label}
    >
      {copied ? (
        <Check className="h-3 w-3 text-green-600" />
      ) : (
        <Copy className="h-3 w-3 text-muted-foreground" />
      )}
    </Button>
  );
}

// =============================================================================
// Column Definitions
// =============================================================================

export const COLUMNS: ColumnDef<Sensor>[] = [
  {
    key: 'image',
    label: 'Image',
    filterType: 'select',
    filterOptions: [
      { label: 'With Photo', value: 'with' },
      { label: 'Without Photo', value: 'without' },
    ],
    getValue: (s) => (s.imageUrl ? 'with' : 'without'),
    render: (s, { onImageClick }) =>
      s.imageUrl ? (
        <img
          src={s.imageUrl}
          alt={s.name}
          className="h-12 w-16 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
          loading="lazy"
          onClick={(e) => {
            e.stopPropagation();
            onImageClick(s.imageUrl!, s.name);
          }}
        />
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      ),
  },
  {
    key: 'name',
    label: 'Sensor',
    sortKey: 'name',
    filterType: 'text',
    getValue: (s) => decodeHtmlEntities(s.name || ''),
    render: (s) => {
      const sensorName = decodeHtmlEntities(s.name || '');
      return (
        <div className="flex items-center gap-1">
          <span className="font-medium">{sensorName || '\u2014'}</span>
          {sensorName && <CopyButton text={sensorName} label={`Copy sensor name: ${sensorName}`} />}
        </div>
      );
    },
  },
  {
    key: 'status',
    label: 'Status',
    sortKey: 'status',
    filterType: 'select',
    filterOptions: ALL_SENSOR_STATUS_CODES.map((code) => ({
      label: ALL_SENSOR_STATUS_MAP[code].label,
      value: String(code),
    })),
    getValue: (s) => String(s.status),
    render: (s) => <SensorStatusBadge status={s.status} />,
  },
  {
    key: 'account',
    label: 'Account',
    getValue: (s) => getPopulatedName(s.organisation),
    render: (s) => {
      const orgName = getPopulatedName(s.organisation);
      return s.accountId ? (
        <Link
          to={`/accounts/${s.accountId}`}
          className="text-blue-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {orgName}
        </Link>
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      );
    },
  },
  {
    key: 'site',
    label: 'Site / Location',
    filterType: 'text',
    getValue: (s) => {
      const site = getPopulatedName(s.site);
      const loc = getPopulatedName(s.location);
      return loc ? `${site} / ${loc}` : site;
    },
    render: (s) => {
      const siteName = getPopulatedName(s.site);
      const locName = getPopulatedName(s.location);
      return (
        <div className="text-sm">
          {siteName || <span className="text-muted-foreground">&mdash;</span>}
          {locName && <span className="text-muted-foreground"> / {locName}</span>}
        </div>
      );
    },
  },
  {
    key: 'stream',
    label: 'Stream',
    filterType: 'text',
    getValue: (s) => decodeHtmlEntities(s.stream_name || ''),
    render: (s) =>
      s.stream_name ? (
        <span className="text-sm">{decodeHtmlEntities(s.stream_name)}</span>
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      ),
  },
  {
    key: 'value',
    label: 'Value',
    getValue: (s) => {
      if (s.value === null || s.value === undefined) return null;
      const unit = s.unit || '';
      return `${s.value}${unit ? ` ${unit}` : ''}`;
    },
    render: (s) => {
      if (s.value === null || s.value === undefined) {
        return <span className="text-muted-foreground">&mdash;</span>;
      }
      return (
        <span className="text-sm font-mono">
          {s.value}
          {s.unit && <span className="text-muted-foreground ml-1">{s.unit}</span>}
        </span>
      );
    },
    align: 'right',
  },
  {
    key: 'lastReading',
    label: 'Last Packet',
    sortKey: 'timestamp',
    filterType: 'date-range',
    getValue: (s) =>
      s.timestamp ? new Date(s.timestamp).toLocaleDateString('en-CA') : '',
    render: (s) => s.timestamp ? formatDateTime(s.timestamp) : '—',
  },
  {
    key: 'sensorType',
    label: 'Sensor Type',
    filterType: 'text',
    getValue: (s) => getPopulatedName(s.sensortype),
    render: (s) => {
      const name = getPopulatedName(s.sensortype);
      return name ? (
        <span className="text-sm">{name}</span>
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      );
    },
  },
  {
    key: 'billable',
    label: 'Billable',
    filterType: 'select',
    filterOptions: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
    getValue: (s) => String(s.billable),
    render: (s) =>
      s.billable ? (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          Yes
        </span>
      ) : (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
          No
        </span>
      ),
    align: 'center',
  },
  {
    key: 'usergroup',
    label: 'User Group',
    filterType: 'text',
    getValue: (s) => getPopulatedName(s.usergroup),
    render: (s) => {
      const name = getPopulatedName(s.usergroup);
      return name ? (
        <span className="text-sm">{name}</span>
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      );
    },
  },
  // Hidden by default columns
  {
    key: 'customId',
    label: 'Custom ID',
    defaultVisible: false,
    filterType: 'text',
    getValue: (s) => decodeHtmlEntities(s.customid || ''),
    render: (s) =>
      s.customid ? (
        <span className="font-mono text-xs">{decodeHtmlEntities(s.customid)}</span>
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      ),
  },
  {
    key: 'inputNumber',
    label: 'Input Number',
    defaultVisible: false,
    getValue: (s) => s.input_number !== null && s.input_number !== undefined ? String(s.input_number) : '',
    render: (s) =>
      s.input_number !== null && s.input_number !== undefined ? (
        <span className="text-sm">{s.input_number}</span>
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      ),
    align: 'center',
  },
  {
    key: 'tags',
    label: 'Tags',
    defaultVisible: false,
    filterType: 'text',
    getValue: (s) => (s.tags || []).map((tag) => decodeHtmlEntities(tag)).join(', '),
    render: (s) => {
      const tags = (s.tags || []).map((tag) => decodeHtmlEntities(tag));
      if (tags.length === 0) return <span className="text-muted-foreground">&mdash;</span>;
      return (
        <div className="flex flex-wrap gap-1">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-muted"
            >
              {tag}
            </span>
          ))}
        </div>
      );
    },
  },
  {
    key: 'actions',
    label: '',
    noFilter: true,
    getValue: () => '',
    render: (s, { onEdit, onReassign }) => (
      <div className="flex items-center gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit(s._id);
          }}
          className="text-atek-blue hover:underline text-sm cursor-pointer"
        >
          Edit
        </button>
        {s.organisation && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReassign(s);
            }}
            className="text-muted-foreground hover:text-foreground cursor-pointer"
            title="Reassign to another account"
          >
            <ArrowRightLeft className="h-4 w-4" />
          </button>
        )}
      </div>
    ),
    align: 'right',
  },
];

// =============================================================================
// Column Visibility Helpers
// =============================================================================

const VISIBILITY_STORAGE_KEY = 'atek-sensors-column-visibility';

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of COLUMNS) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

export function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch {
    /* ignore */
  }
  return getDefaultVisibility();
}

export function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

// =============================================================================
// Filter Row Renderer
// =============================================================================

function renderColumnFilter<T>(
  col: ColumnDef<T>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  if (col.key === 'account') {
    const rawValue = columnFilters[col.key] || '';
    const accountFilterValue =
      !rawValue || rawValue.trim().startsWith('[') ? 'all' : rawValue;

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <SensorAccountsFilterCombobox
          value={accountFilterValue}
          onChange={(accountId) => {
            setColumnFilter(col.key, accountId === 'all' ? '' : accountId);
          }}
        />
      </TableHead>
    );
  }

  // Multi-select filter
  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try {
        return columnFilters[col.key]
          ? JSON.parse(columnFilters[col.key])
          : [];
      } catch {
        return [];
      }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-full text-xs justify-start font-normal"
            >
              {selected.length ? `${selected.length} selected` : '\u2014'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  // Date range filter
  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={from}
            onChange={(e) =>
              setColumnFilter(col.key, `${e.target.value}>>${to}`)
            }
            title="From"
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={to}
            onChange={(e) =>
              setColumnFilter(col.key, `${from}>>${e.target.value}`)
            }
            title="To"
          />
        </div>
      </TableHead>
    );
  }

  // Number range filter
  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) =>
              setColumnFilter(col.key, `${e.target.value}>>${max}`)
            }
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) =>
              setColumnFilter(col.key, `${min}>>${e.target.value}`)
            }
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  // Text filter (default)
  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// =============================================================================
// SortableHeader
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey?: string;
  sortDirection?: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Table Component
// =============================================================================

interface SensorsTableProps {
  sensors: Sensor[];
  pageCount?: number;
  isLoading: boolean;
  total: number;
  sortKey: string;
  sortDirection: 1 | -1;
  onSort: (key: string) => void;
  onEditSensor: (sensorId: string) => void;
  onReassignSensor: (sensor: Sensor) => void;
  columnFilters: Record<string, string>;
  setColumnFilter: (key: string, value: string) => void;
  columnVisibility: Record<string, boolean>;
  toggleColumn: (key: string) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onExportCSV: () => void;
  onCopyClipboard: () => void;
  onClearColumnFilters?: () => void;
}

export function SensorsTable({
  sensors,
  pageCount,
  isLoading,
  total,
  sortKey,
  sortDirection,
  onSort,
  onEditSensor,
  onReassignSensor,
  columnFilters,
  setColumnFilter,
  columnVisibility,
  toggleColumn,
  page,
  totalPages,
  onPageChange,
  onExportCSV,
  onCopyClipboard,
  onClearColumnFilters,
}: SensorsTableProps) {
  const [lightbox, setLightbox] = useState<{ url: string; name: string } | null>(null);
  const tableHelpers: SensorTableHelpers = {
    onEdit: onEditSensor,
    onReassign: onReassignSensor,
    onImageClick: (url, name) => setLightbox({ url, name }),
  };
  const hasActiveColumnFilters = Object.values(columnFilters).some(Boolean);
  const isVisible = (key: string) => columnVisibility[key] !== false;
  const visibleColumns = COLUMNS.filter((col) => isVisible(col.key));

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {pageCount !== undefined && sensors.length < pageCount
              ? `Showing ${sensors.length} of ${pageCount} on this page`
              : `Showing ${sensors.length} of ${total}`}
          </span>
          <div className="flex items-center gap-2">
            {hasActiveColumnFilters && onClearColumnFilters && (
              <Button variant="outline" size="sm" onClick={onClearColumnFilters}>
                <FilterX className="h-4 w-4 mr-1" /> Clear filters
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onCopyClipboard}>
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
            <Button variant="outline" size="sm" onClick={onExportCSV}>
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings2 className="h-4 w-4 mr-1" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {COLUMNS.filter((c) => !c.noFilter).map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={isVisible(col.key)}
                    onCheckedChange={() => toggleColumn(col.key)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {col.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {/* ROW 1: Filter inputs */}
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) =>
                  renderColumnFilter(col, columnFilters, setColumnFilter),
                )}
              </TableRow>

              {/* ROW 2: Column labels + sort */}
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead
                    key={col.key}
                    className={cn(col.align === 'right' && 'text-right')}
                  >
                    {col.sortKey ? (
                      <SortableHeader
                        label={col.label}
                        sortKey={col.sortKey}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={onSort}
                      />
                    ) : (
                      col.label
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Loading state */}
              {isLoading &&
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    {visibleColumns.map((col) => (
                      <TableCell key={col.key}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {/* Empty state */}
              {!isLoading && sensors.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length}
                    className="py-12 text-center text-muted-foreground"
                  >
                    No sensors found
                  </TableCell>
                </TableRow>
              )}

              {/* Data rows */}
              {!isLoading &&
                sensors.map((sensor) => (
                  <TableRow
                    key={sensor._id}
                    className="hover:bg-muted/50"
                  >
                    {visibleColumns.map((col) => (
                      <TableCell
                        key={col.key}
                        className={cn(
                          'text-sm',
                          col.align === 'right' && 'text-right tabular-nums',
                          col.align === 'center' && 'text-center',
                          col.className,
                        )}
                      >
                        {col.render
                          ? col.render(sensor, tableHelpers)
                          : String(col.getValue(sensor) ?? '') || '\u2014'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(page - 1)}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages - 1}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {/* Image Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(open) => { if (!open) setLightbox(null); }}>
        <DialogContent className="max-w-3xl p-2">
          {lightbox && (
            <img
              src={lightbox.url}
              alt={lightbox.name}
              className="w-full h-auto max-h-[85vh] object-contain rounded"
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
